/**
 * Per-canvas "session restore" snapshot in `sessionStorage`: the chat the user
 * had open and the right detail pane (route + per-doc scroll). Restored on tab
 * switch so a canvas resumes where you left off. Session-only, like the separate
 * `canvas-viewport:` store. Slices have different owners, so writers
 * read-modify-write.
 */

const STORAGE_PREFIX = "canvas-session:";

// Canvases whose tab was just closed: snapshot cleared and further writes
// suppressed until the canvas is reopened (mirrors the viewport hook, so a
// trailing unmount/debounce write can't resurrect a just-cleared snapshot).
const suppressedCanvasIds = new Set<string>();

export interface CanvasSessionChat {
  /** Chat window up (vs FAB only). */
  open: boolean;
  /** Active (top-of-stack) session id, or null. */
  sessionId: string | null;
}

export interface CanvasSessionRightPane {
  /** PaneLocation pathname to reopen (e.g. "/file-record/{id}"). */
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
}

export interface CanvasSessionSnapshot {
  chat?: CanvasSessionChat;
  rightPane?: CanvasSessionRightPane | null;
  /** scrollTop keyed by right-pane content id (file record / session id). */
  scroll?: Record<string, number>;
}

/** Synchronous read; returns null if missing or invalid. */
export function readCanvasSession(
  canvasId: string,
): CanvasSessionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + canvasId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as CanvasSessionSnapshot;
    }
    return null;
  } catch {
    return null;
  }
}

// Single suppression gate: read-modify-write a slice into the snapshot. No-op
// for a just-closed canvas until it's reopened.
function mergeCanvasSession(
  canvasId: string,
  patch: Partial<CanvasSessionSnapshot>,
) {
  if (suppressedCanvasIds.has(canvasId)) return;
  const next = { ...(readCanvasSession(canvasId) ?? {}), ...patch };
  try {
    sessionStorage.setItem(STORAGE_PREFIX + canvasId, JSON.stringify(next));
  } catch {
    // quota exceeded / unavailable — ignore.
  }
}

export function writeCanvasSessionChat(
  canvasId: string,
  chat: CanvasSessionChat,
) {
  mergeCanvasSession(canvasId, { chat });
}

export function writeCanvasSessionRightPane(
  canvasId: string,
  rightPane: CanvasSessionRightPane | null,
) {
  mergeCanvasSession(canvasId, { rightPane });
}

export function readCanvasSessionScroll(
  canvasId: string,
  contentId: string,
): number | null {
  const v = readCanvasSession(canvasId)?.scroll?.[contentId];
  return typeof v === "number" ? v : null;
}

export function writeCanvasSessionScroll(
  canvasId: string,
  contentId: string,
  scrollTop: number,
) {
  const prev = readCanvasSession(canvasId) ?? {};
  mergeCanvasSession(canvasId, {
    scroll: { ...(prev.scroll ?? {}), [contentId]: scrollTop },
  });
}

/**
 * Clears a canvas's snapshot — call when its tab is closed so a later reopen
 * starts clean. Also suppresses any trailing write until the canvas is reopened.
 */
export function clearCanvasSession(canvasId: string) {
  suppressedCanvasIds.add(canvasId);
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + canvasId);
  } catch {
    // unavailable — ignore.
  }
}

/** Re-enable persistence for a (re)opened canvas. */
export function unsuppressCanvasSession(canvasId: string) {
  suppressedCanvasIds.delete(canvasId);
}
